import type {
    DinoTutorContextPatch,
    DinoTutorExerciseType
} from "../mascot/context/index.js";

const EXERCISE_CONTEXT_SOURCE = "exercise-runner";

interface SafeExerciseTutorQuestion {
    readonly prompt: string;
    readonly type: DinoTutorExerciseType;
}

interface ExerciseTutorContextInput {
    readonly exerciseId: string;
    readonly objective: string;
    readonly question?: SafeExerciseTutorQuestion;
    readonly questionIndex: number;
    readonly questionCount: number;
    readonly outcome: "correct" | "incorrect" | null;
    readonly finished: boolean;
}

function boundedText(value: string, maximumLength: number): string {
    return value
        .replace(/\s+/gu, " ")
        .trim()
        .slice(0, maximumLength);
}

/**
 * Builds the only exercise payload visible to Dino.
 *
 * Correct answers, proposed options and learner answers are deliberately
 * absent from this input contract, so a hint engine cannot receive them by
 * accident.
 */
function buildExerciseTutorContext({
    exerciseId,
    objective,
    question,
    questionIndex,
    questionCount,
    outcome,
    finished
}: ExerciseTutorContextInput): DinoTutorContextPatch {
    const safeCount = Math.max(0, Math.trunc(questionCount));
    const safeIndex = Math.max(0, Math.min(
        Math.trunc(questionIndex),
        Math.max(0, safeCount - 1)
    ));
    const completedQuestions = finished
        ? safeCount
        : safeIndex + (outcome === null ? 0 : 1);
    const progress = safeCount === 0
        ? 0
        : completedQuestions / safeCount;

    return {
        exercise: {
            id: boundedText(exerciseId, 160),
            phase: finished || outcome !== null ? "completed" : "active",
            progress,
            objective: boundedText(objective, 200),
            questionIndex: safeIndex,
            questionCount: safeCount,
            ...(question
                ? {
                    type: question.type,
                    prompt: boundedText(question.prompt, 600)
                }
                : {}),
            ...(outcome ? { outcome } : {})
        }
    };
}

export {
    EXERCISE_CONTEXT_SOURCE,
    buildExerciseTutorContext
};
export type {
    ExerciseTutorContextInput,
    SafeExerciseTutorQuestion
};
